import React, { useState } from 'react';
import { useAuthStore } from '@passport/store';
import { PasswordField } from './PasswordField';
import { checkPasswordRequirements } from './passwordRequirements';
import './pages.css';

// Reuses @passport/store's changePassword action directly (not @passport/auth-core's own
// ChangePasswordPage component, which operates on auth-core's separate store — the one this
// whole app ecosystem has moved away from, see ProfilePage.tsx's own history for the same
// @admin/api decoupling reasoning). This keeps the page consistent with every other
// @passport/pages page (ProfilePage included), which all read/act on @passport/store.
export interface ChangePasswordPageProps {
  /**
   * Opt-in. Also requires the current password, marks the offending field `aria-invalid`, links the
   * alert to it and moves focus there. Off by default so other hosts keep their existing behaviour.
   */
  fieldValidation?: boolean;
}

export const ChangePasswordPage: React.FC<ChangePasswordPageProps> = ({ fieldValidation = false }) => {
  const { changePassword, isLoading, error, clearError } = useAuthStore();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [invalidField, setInvalidField] = useState<'current-password' | 'new-password' | 'confirm-password' | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const clearInvalid = (field: 'current-password' | 'new-password' | 'confirm-password') => {
    if (invalidField === field) setInvalidField(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    setInvalidField(null);
    clearError();

    const fail = (field: NonNullable<typeof invalidField>, message: string) => {
      setValidationError(message);
      if (!fieldValidation) return;
      setInvalidField(field);
      document.getElementById(field)?.focus();
    };

    if (fieldValidation && !currentPassword) {
      fail('current-password', 'Enter your current password.');
      return;
    }
    if (!checkPasswordRequirements(newPassword).allMet) {
      fail('new-password', 'New password does not meet all the password requirements.');
      return;
    }
    if (newPassword !== confirmPassword) {
      fail('confirm-password', 'New password and confirmation do not match.');
      return;
    }

    // changePassword() logs the user out on success (invalidates the old session) — see
    // authStore.ts. Show a confirmation instead of immediately bouncing to the sign-in
    // redirect, so the user understands why they're about to be signed out.
    const success = await changePassword(currentPassword, newPassword);
    if (success) {
      setSucceeded(true);
    }
  };

  if (succeeded) {
    return (
      <div className="admin-page-container">
        <div className="page-header">
          <h1 className="page-title">Change Password</h1>
        </div>
        <div className="page-card">
          <div className="page-card-body">
            <div className="page-alert page-alert-success">
              <span className="page-alert-message">
                Password changed successfully. You've been signed out for security — please sign
                in again with your new password.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <div className="page-header">
        <h1 className="page-title">Change Password</h1>
        <p className="page-subtitle">Update the password for your account</p>
      </div>

      <div className="page-card">
        <div className="page-card-body">
          <form onSubmit={handleSubmit} className="page-form" noValidate>
            {(validationError || error) && (
              <div className="page-alert page-alert-error" role="alert" id="change-password-error">
                <span className="page-alert-message">{validationError || error}</span>
              </div>
            )}

            <div className="page-form-group">
              <label htmlFor="current-password" className="page-form-label required">
                Current Password
              </label>
              <PasswordField
                id="current-password"
                aria-invalid={invalidField === 'current-password'}
                aria-describedby={invalidField === 'current-password' ? 'change-password-error' : undefined}
                value={currentPassword}
                onChange={(value) => { setCurrentPassword(value); clearInvalid('current-password'); }}
                autoComplete="current-password"
                disabled={isLoading}
              />
            </div>

            <div className="page-form-group">
              <label htmlFor="new-password" className="page-form-label required">
                New Password
              </label>
              <PasswordField
                id="new-password"
                aria-invalid={invalidField === 'new-password'}
                aria-describedby={invalidField === 'new-password' ? 'change-password-error' : undefined}
                value={newPassword}
                onChange={(value) => { setNewPassword(value); clearInvalid('new-password'); }}
                autoComplete="new-password"
                disabled={isLoading}
                placeholder="At least 8 characters"
                showRequirements
              />
            </div>

            <div className="page-form-group">
              <label htmlFor="confirm-password" className="page-form-label required">
                Confirm New Password
              </label>
              <PasswordField
                id="confirm-password"
                aria-invalid={invalidField === 'confirm-password'}
                aria-describedby={invalidField === 'confirm-password' ? 'change-password-error' : undefined}
                value={confirmPassword}
                onChange={(value) => { setConfirmPassword(value); clearInvalid('confirm-password'); }}
                autoComplete="new-password"
                disabled={isLoading}
              />
            </div>

            <div className="page-btn-group">
              <button type="submit" className="page-btn page-btn-primary" disabled={isLoading}>
                {isLoading ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
